import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import TopTabbar from "../components/partials/TopTabbarIndex";
import Header from "../components/partials/Header";
import { parameterInstance } from '../utils/axiosConfig';
import LoadingSpinner from '../utils/LoadingSpinner'; // Import de votre composant de chargement personnalisé

const IndexPage = () => {
    // États
    const [isLoading, setIsLoading] = useState(true);
    const [rates, setRates] = useState([]);
    const [error, setError] = useState(null);
    const [openItems, setOpenItems] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);

    // Références pour les sections animées
    const observerRef = useRef(null);
    const sectionRefsRef = useRef([]);

    // Textes du diaporama
    const texts = [
        {
            title: "Transférez de l'argent en toute simplicité",
            description: "Envoyez de l'argent en toute sécurité et rapidement à vos proches en Afrique."
        },
        {
            title: "Rejoignez la communauté NTR Finance",
            description: "Profitez de transferts d'argent rapides et sécurisés."
        },
        {
            title: "Simplifiez vos transferts internationaux",
            description: "Avec NTR Finance, vos transferts sont simplifiés et sécurisés."
        },
        {
            title: "Soutenez vos proches en Afrique",
            description: "Envoyez de l'argent en toute confiance avec NTR Finance."
        }
    ];

    // Hook pour le diaporama de texte
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % texts.length);
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    // Hook pour les animations de défilement
    useEffect(() => {
        const animatedSections = document.querySelectorAll('.ip-animated-section');
        sectionRefsRef.current = Array.from(animatedSections);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                } else {
                    entry.target.classList.remove('is-visible');
                }
            });
        }, observerOptions);

        sectionRefsRef.current.forEach(section => {
            if (section) {
                observerRef.current.observe(section);
            }
        });

        return () => {
            if (observerRef.current) {
                sectionRefsRef.current.forEach(section => {
                    if (section) {
                        observerRef.current.unobserve(section);
                    }
                });
            }
        };
    }, []);

    // Récupération des taux des pays
    useEffect(() => {
        const fetchRates = async () => {
            try {
                setIsLoading(true);
                const response = await parameterInstance.get('/findAll');
                if (response.data.length > 0) {
                    setRates(response.data[0].paramRates);
                }
                setError(null);
            } catch (error) {
                setError('Erreur lors de la récupération des taux. Veuillez réessayer plus tard.');
                console.error('Erreur lors de la récupération des taux :', error.message || error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRates();
    }, []);

    // Fonction pour basculer l'état d'une question de la FAQ
    const toggleItem = (categoryIndex, questionIndex) => {
        setOpenItems((prev) => ({
            ...prev,
            [`${categoryIndex}-${questionIndex}`]: !prev[`${categoryIndex}-${questionIndex}`],
        }));
    };

    // Gestion de l'erreur
    if (error) {
        return (
            <div className="error-container text-center py-5">
                <h2>Une erreur est survenue</h2>
                <p>{error}</p>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => window.location.reload()}
                >
                    Réessayer
                </button>
            </div>
        );
    }

    // Styles CSS intégrés
    const styles = `
        :root {
            --primary-color: #24afcc;
            --secondary-color: #29649c;
            --accent-color: #ffd700;
            --dark-color: #1a2a3a;
            --light-color: #f8f9fa;
            --gradient-primary: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
            --gradient-accent: linear-gradient(90deg, var(--accent-color), var(--primary-color));
        }

        /* Typographie moderne */
        body {
            font-family: 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
            line-height: 1.6;
            color: #333;
        }

        h1, h2, h3, h4, h5, h6 {
            font-weight: 700;
            line-height: 1.2;
        }

        /* Animations de défilement améliorées */
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .ip-animated-section {
            opacity: 0;
            transform: translateY(30px);
            transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                        transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .ip-animated-section.is-visible {
            opacity: 1;
            transform: translateY(0);
        }

        /* Section Hero améliorée */
        .hero-section {
            background: var(--gradient-primary), url('assets/images/bg/6.jpg');
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            background-blend-mode: overlay;
            color: white;
            padding: 6rem 0;
            position: relative;
            overflow: hidden;
        }

        .hero-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: url('assets/images/bg/pattern.svg') no-repeat;
            background-size: cover;
            opacity: 0.1;
        }

        .hero-content {
            position: relative;
            z-index: 2;
        }

        .hero-title {
            font-size: 3rem;
            font-weight: 800;
            margin-bottom: 1.5rem;
            text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .hero-description {
            font-size: 1.25rem;
            margin-bottom: 2rem;
            opacity: 0.9;
        }

        .cta-button {
            background: var(--gradient-accent);
            border: none;
            color: white;
            font-weight: 600;
            padding: 1rem 2rem;
            border-radius: 50px;
            font-size: 1.25rem;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
            display: inline-block;
        }

        .cta-button:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        /* Cartes modernes */
        .feature-card {
            border: none;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 10px 20px rgba(0,0,0,0.05);
            transition: all 0.3s ease;
            background: white;
        }

        .feature-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(0,0,0,0.1);
        }

        .feature-icon {
            font-size: 2.5rem;
            margin-bottom: 1rem;
            background: var(--gradient-primary);
            -webkit-background-clip: text;
            background-clip: text;
        }

        /* Section pays */
        .countries-section {
            background: url('assets/images/bg/world-map.png') no-repeat center center;
            background-size: cover;
            position: relative;
            padding: 5rem 0;
        }

        .countries-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: url(assets/images/bg/6.jpg);
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;
        }

        .country-card {
            background: rgba(255,255,255,0.1);
            backdrop-filter: blur(10px);
            border-radius: 12px;
            padding: 2rem;
            text-align: center;
            transition: all 0.3s ease;
            border: 1px solid rgba(255,255,255,0.1);
        }

        .country-card:hover {
            transform: translateY(-5px);
            background: rgba(255,255,255,0.15);
        }

        /* FAQ améliorée */
        .faq-item {
            margin-bottom: 1rem;
        }

        .faq-question {
            background: white;
            border-radius: 8px;
            padding: 1.25rem;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            border-left: 4px solid var(--primary-color);
        }

        .faq-question:hover {
            box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }

        .faq-answer {
            background: #f8fafc;
            border-radius: 0 0 8px 8px;
            padding: 1.25rem;
            margin-top: -5px;
            border-left: 4px solid var(--primary-color);
        }

        /* Section témoignages */
        .testimonial-card {
            background: white;
            border-radius: 12px;
            padding: 2rem;
            box-shadow: 0 5px 15px rgba(0,0,0,0.05);
            position: relative;
        }

        .testimonial-card::before {
            content: '"';
            position: absolute;
            top: 1rem;
            left: 1rem;
            font-size: 4rem;
            color: rgba(36, 175, 204, 0.1);
            font-family: serif;
            line-height: 1;
        }

        /* Pied de page */
        .footer {
            background: var(--dark-color);
            color: white;
            padding: 4rem 0 2rem;
        }

        .footer-links a {
            color: rgba(255,255,255,0.7);
            transition: color 0.3s ease;
        }

        .footer-links a:hover {
            color: white;
            text-decoration: none;
        }

        .contact-section-with-bg {
            position: relative;
            background: url(assets/images/bg/6.jpg) no-repeat center center;
            background-size: cover;
            z-index: 1;
        }

        .contact-section-with-bg::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.6);
            z-index: -1;
        }

        /* Responsive */
        @media (max-width: 768px) {
            .hero-title {
                font-size: 2.25rem;
            }

            .hero-description {
                font-size: 1rem;
            }
        }
    `;

    return (
        <div className="ip-scroll-container">
            {isLoading && <LoadingSpinner isVisible={true} loadingText="Chargement..." />}
            <style>{styles}</style>
            <TopTabbar/>
            <Header/>

            {/* Section Hero modernisée */}
            <section className="hero-section ip-animated-section">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 hero-content">
                            <div className="mb-5">
                                <h1 className="hero-title">{texts[currentIndex].title}</h1>
                                <p className="hero-description">{texts[currentIndex].description}</p>
                            </div>
                            <Link to="/login">
                                <button className="cta-button mb-3">
                                    Commencer maintenant
                                </button>
                            </Link>
                        </div>
                        <div className="col-lg-6">
                            <div className="text-center">
                                <img
                                    style={{
                                        height: 'auto',
                                        width: '100%',
                                        maxWidth: '600px',
                                        borderRadius: '12px',
                                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
                                    }}
                                    src="assets/images/banner/banner.png"
                                    alt="Transfert d'argent du Canada vers l'Afrique"
                                    className="img-fluid"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Étapes pour envoyer de l'argent */}
            <section className="py-6 ip-animated-section bg-light">
                <div className="container p-3">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold">Envoyer de l'argent en 4 étapes simples</h2>
                        <p className="lead text-muted">Un processus rapide et sécurisé pour soutenir vos proches</p>
                    </div>
                    <div className="row g-4 justify-content-center">
                        {[
                            {
                                icon: "📱",
                                title: "Créer un compte",
                                description: "Inscrivez-vous en quelques clics pour commencer."
                            },
                            {
                                icon: "🆔",
                                title: "Identification",
                                description: "Soumettez vos informations d'identification."
                            },
                            {
                                icon: "👨‍👩‍👧‍👦",
                                title: "Ajouter un bénéficiaire",
                                description: "Entrez les informations de votre proche."
                            },
                            {
                                icon: "💸",
                                title: "Envoyer de l'argent",
                                description: "Effectuez le transfert en quelques minutes."
                            }
                        ].map((step, index) => (
                            <div key={index} className="col-md-6 col-lg-3 process">
                                <div className="feature-card h-100 p-4 text-center">
                                    <div className="feature-icon" style={{fontSize: '2.5rem'}}>{step.icon}</div>
                                    <h3 className="h5 mb-3 ">{step.title}</h3>
                                    <p className="text-muted mb-0">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section Pays Destinataire */}
            <section className="countries-section ip-animated-section">
                <div className="container position-relative">
                    <div className="text-center mb-5">
                        <h2 className="text-white display-5 fw-bold">Pays Destinataires</h2>
                        <p className="lead text-white-50">Envoyez de l'argent dans plus 4 pays africains avec des taux
                            compétitifs</p>
                    </div>
                    <div className="row g-4 justify-content-center">
                        {rates.map((rate, index) => (
                            <div key={index} className="col-md-6 col-lg-3">
                                <div className="country-card h-100">
                                    <img
                                        style={{height: 'auto', width: '80px', margin: '0 auto 1rem'}}
                                        src={`/assets/svg/pays/${rate.country.toLowerCase().replace(" ", "-").replace("ô", "o").replace("'", "")}.svg`}
                                        alt={rate.country}
                                        className="flag-icon mb-3"
                                    />
                                    <h3 className="h5 text-white mb-3">{rate.country}</h3>
                                    <p className="text-white-50 mb-0">Taux : 1 CAD
                                        = {rate.rate} {rate.country === "Cameroun" ? "XAF" : "XOF"}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section Pourquoi choisir NTR Finance ? */}
            <section className="py-6 ip-animated-section mt-5 mb-5">
                <div className="container pb-5">
                    <div className="text-center mb-5">
                        <h2 className="display-5 fw-bold">Pourquoi choisir NTR Finance ?</h2>
                        <p className="lead text-muted">Nous offrons la meilleure expérience de transfert d'argent</p>
                    </div>
                    <div className="row g-4">
                        {[
                            {
                                icon: "💱",
                                title: "Taux de change compétitifs",
                                description: "Profitez de taux de change avantageux qui vous permettent de maximiser la valeur de votre argent."
                            },
                            {
                                icon: "💡",
                                title: "Transparence des frais",
                                description: "Aucun frais - vous savez exactement ce que vous payez avant chaque transfert."
                            },
                            {
                                icon: "⚡",
                                title: "Rapidité",
                                description: "Transferts instantanés vers les portefeuilles mobiles, en quelques heures pour les retraits en espèces."
                            },
                            {
                                icon: "🔒",
                                title: "Sécurité bancaire",
                                description: "Protection de niveau bancaire pour toutes vos transactions et informations personnelles."
                            },
                            {
                                icon: "🛡️",
                                title: "Fiabilité",
                                description: "Service fiable avec des années d'expérience dans les transferts internationaux."
                            },
                            {
                                icon: "📞",
                                title: "Support 24/7",
                                description: "Notre équipe est disponible à tout moment pour répondre à vos questions."
                            }
                        ].map((feature, index) => (
                            <div key={index} className="col-md-6 col-lg-4 process">
                                <div className="feature-card h-100 p-4">
                                    <div className="feature-icon" style={{fontSize: '2rem'}}>{feature.icon}</div>
                                    <h3 className="h5 mb-3">{feature.title}</h3>
                                    <p className="text-muted mb-0">{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section Comment envoyer/recevoir de l'argent combinée */}
            <section className="py-6 ip-animated-section bg-light mb-5">
                <div className="container mb-5 pb-5">
                    <div className="row g-5">
                        <div className="col-lg-6">
                            <div className="feature-card p-5 h-100 process2">
                                <h2 className="display-6 fw-bold mb-4">Comment envoyer de l'argent</h2>
                                <div className="d-flex align-items-start mb-4">
                                    <div className="feature-icon me-4" style={{fontSize: '1.5rem'}}>💻</div>
                                    <div>
                                        <h3 className="h5">Envoi en ligne</h3>
                                        <p className="text-muted">Utilisez notre site web pour vérifier les taux et
                                            envoyer de l'argent depuis le confort de votre maison.</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-start">
                                    <div className="feature-icon me-4" style={{fontSize: '1.5rem'}}>📱</div>
                                    <div>
                                        <h3 className="h5">Application mobile</h3>
                                        <p className="text-muted">Envoyez et suivez vos transferts d'argent où que vous
                                            soyez avec notre application mobile.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="feature-card p-5 h-100 process2">
                                <h2 className="display-6 fw-bold mb-4">Comment recevoir de l'argent</h2>
                                <div className="d-flex align-items-start mb-4">
                                    <div className="feature-icon me-4" style={{fontSize: '1.5rem'}}>💸</div>
                                    <div>
                                        <h3 className="h5">Retrait en espèces</h3>
                                        <p className="text-muted">Retirez votre argent dans nos agences au
                                            Cameroun à Douala.</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-start mb-4">
                                    <div className="feature-icon me-4" style={{fontSize: '1.5rem'}}>🏦</div>
                                    <div>
                                        <h3 className="h5">Compte bancaire</h3>
                                        <p className="text-muted">Recevez directement sur un compte bancaire (uniquement
                                            disponible au Cameroun).</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-start">
                                    <div className="feature-icon me-4" style={{fontSize: '1.5rem'}}>📲</div>
                                    <div>
                                        <h3 className="h5">Portefeuille mobile</h3>
                                        <p className="text-muted">Recevez instantanément sur votre portefeuille
                                            électronique mobile.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Notre application mobile */}
            <section className="py-6 ip-animated-section">
                <div className="container pb-3">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <h2 className="display-5 fw-bold mb-4">Notre application mobile</h2>
                            <p className="lead mb-4">Téléchargez notre application pour effectuer des transferts en
                                quelques clics, partout au Canada.</p>
                            <div className="row">
                                <div className="col-6">
                                    <a href="https://apps.apple.com/app/ntr-finance/id6749550208" target="_blank"
                                       rel="noopener noreferrer" className="d-block">
                                        <img
                                            src="/assets/svg/pays/apple-store.svg"
                                            alt="Apple Store"
                                            className="img-fluid"
                                        />
                                    </a>
                                </div>
                                <div className="col-6">
                                    <a href="https://play.google.com/store/apps/details?id=com.ntrfinance.app"
                                       target="_blank" rel="noopener noreferrer" className="d-block">
                                        <img
                                            src="/assets/svg/pays/google-play.svg"
                                            alt="Google Play Store"
                                            className="img-fluid"
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-6 mt-5 mt-lg-0">
                            <img
                                src="assets/images/mobile-app/1.png"
                                alt="Application mobile NTR Finance"
                                className="img-fluid rounded-lg shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Sécurité et confidentialité */}
            <section className="py-6 ip-animated-section bg-light pt-5 pb-5">
                <div className="container text-center">
                    <h2 className="display-5 fw-bold mb-4">Sécurité et confidentialité</h2>
                    <p className="lead mx-auto mb-5" style={{maxWidth: '700px'}}>
                        NTR Finance utilise un cryptage de niveau bancaire et des protocoles de sécurité avancés pour
                        protéger vos données.
                        Nous sommes conformes aux réglementations canadiennes sur les services financiers et la
                        protection des données.
                    </p>
                    <div className="row g-4 justify-content-center">
                        {[
                            {
                                icon: "🔐",
                                title: "Cryptage AES-256",
                                description: "Toutes vos données sont cryptées avec le standard bancaire"
                            },
                            {
                                icon: "📜",
                                title: "Conforme à la réglementation",
                                description: "Respect strict des lois canadiennes sur les transferts d'argent"
                            },
                            {
                                icon: "👁️",
                                title: "Vie privée",
                                description: "Vos données ne sont jamais partagées avec des tiers"
                            }
                        ].map((item, index) => (
                            <div key={index} className="col-md-4">
                                <div className="feature-card p-4 h-100 process2">
                                    <div className="feature-icon" style={{fontSize: '2rem'}}>{item.icon}</div>
                                    <h3 className="h5 mb-3">{item.title}</h3>
                                    <p className="text-muted mb-0">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section FAQ */}
            <section className="py-6 ip-animated-section">
                <div className="container mb-5">
                    <div className="text-center mt-5">
                        <h2 className="display-5 fw-bold">Questions fréquentes</h2>
                        <p className="lead text-muted">Trouvez des réponses à vos questions sur nos services</p>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            {[
                                {
                                    category: "Transferts d'Argent",
                                    questions: [
                                        {
                                            "question": "Comment envoyer de l'argent avec NTR Finance ?",
                                            "answer": "Le processus est simple : <br />1) <b>Connectez-vous à votre compte,</b> <br />2) <b>Sélectionnez 'Transfert',</b> <br />3) <b>Choisissez le pays destinataire (Cameroun, Burkina Faso, Sénégal, Bénin ou Côte d'Ivoire),</b> <br />4) <b>Entrez les informations du bénéficiaire et le montant,</b> <br />5) <b>Effectuez le virement hors de l'application,</b> <br />6) <b>Après avoir effectué le virement Interac hors de l'application, revenez quelques instants plus tard dans l'application pour vérifier le statut de votre transfert.</b>"
                                        },
                                        {
                                            "question": "Quels sont les délais de transfert ?",
                                            "answer": "Avec NTR Finance, vos transferts sont traités rapidement : <br />1) <b>En 10 minutes pour les transferts vers les portefeuilles mobiles,</b> <br />2) <b>En quelques heures pour les retraits en espèces,</b> <br />3) <b>En 1-2 jours ouvrables pour les transferts bancaires.</b>"
                                        },
                                    ]
                                },
                                {
                                    category: "Frais et Limites",
                                    questions: [
                                        {
                                            question: "Quels sont les frais de transfert ?",
                                            answer: "Aucun frais. Le montant exact du transfert vous est toujours présenté avant la confirmation de votre transfert."
                                        },
                                        {
                                            question: "Y a-t-il des limites de transfert ?",
                                            answer: "Vous pouvez envoyer jusqu'à 5 000 CAD par transfert."
                                        }
                                    ]
                                },
                                {
                                    category: "Sécurité",
                                    questions: [
                                        {
                                            question: "Comment NTR Finance protège mes informations ?",
                                            answer: "NTR Finance utilise un cryptage de niveau bancaire et des protocoles de sécurité avancés. Nous sommes conformes aux réglementations canadiennes sur les services financiers et la protection des données."
                                        },
                                        {
                                            question: "Que faire en cas de problème ?",
                                            answer: "Notre service client est disponible 24/7. Contactez-nous par <a href='https://wa.me/+15879894933' target='_blank'>WhatsApp</a>, par email à <a href='mailto:support@ntrfinancial.com' target='_blank'>support@ntrfinancial.com</a> ou par téléphone au <a href='tel:+15879894933' target='_blank'>+1 587-989-4933</a>."
                                        }
                                    ]
                                },
                                {
                                    category: "Modes de Paiement",
                                    questions: [
                                        {
                                            question: "Quels moyens de paiement acceptez-vous ?",
                                            answer: "Nous acceptons les virements bancaires, Interac, et les principaux portefeuilles électroniques canadiens."
                                        },
                                        {
                                            question: "Comment le bénéficiaire reçoit l'argent ?",
                                            "answer": "Les options de réception incluent :\n- Le retrait en espèces dans nos agences au Cameroun à Douala.\n- Le dépôt sur compte bancaire (uniquement disponible au cameroun).\n- Les portefeuilles électroniques locaux."
                                        }
                                    ]
                                }
                            ].map((category, categoryIndex) => (
                                <div key={categoryIndex} className="mb-5">
                                    <h3 className="h4 mb-4 text-primary">{category.category}</h3>
                                    {category.questions.map((item, questionIndex) => (
                                        <div key={questionIndex} className="faq-item">
                                            <div
                                                className="faq-question"
                                                onClick={() => toggleItem(categoryIndex, questionIndex)}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h4 className="mb-0 h5">{item.question}</h4>
                                                    <span
                                                        className={`arrow-icon ${openItems[`${categoryIndex}-${questionIndex}`] ? 'rotate-180' : ''}`}>
                                                        ▼
                                                    </span>
                                                </div>
                                            </div>
                                            {openItems[`${categoryIndex}-${questionIndex}`] && (
                                                <div className="faq-answer">
                                                    <p className="mb-0"
                                                       dangerouslySetInnerHTML={{__html: item.answer.replace(/\n/g, '<br />')}}></p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Section Contact */}
            <section className="contact-section-with-bg py-6 ip-animated-section text-white">
                <div className="container">
                    <div className="text-center mb-5 pt-5 pb-3">
                        <h2 className="display-5 fw-bold">Besoin d'aide ?</h2>
                        <p className="lead opacity-75">Notre équipe est disponible 24/7 pour répondre à vos
                            questions</p>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="row g-4">
                                {[
                                    {
                                        icon: "assets/svg/whatsapp-icon.svg",
                                        title: "WhatsApp",
                                        link: "https://wa.me/+15879894933",
                                        value: "+1 587-989-4933"
                                    },
                                    {
                                        icon: "assets/svg/mail-icon.svg",
                                        title: "Email",
                                        link: "mailto:support@ntrfinancial.com",
                                        value: "support@ntrfinancial.com"
                                    },
                                    {
                                        icon: "assets/svg/phone-icon.svg",
                                        title: "Téléphone",
                                        link: "tel:+15879894933",
                                        value: "+1 587-989-4933"
                                    },
                                    {
                                        icon: "assets/svg/facebook-icon.svg",
                                        title: "Facebook",
                                        link: "https://www.facebook.com/profile.php?id=61563752121174",
                                        value: "NTR Finance"
                                    }
                                ].map((contact, index) => (
                                    <div key={index} className="col-md-6">
                                        <a href={contact.link} target="_blank" rel="noopener noreferrer"
                                           className="text-white text-decoration-none">
                                            <div className="bg-white-10 rounded-lg p-4 h-100 d-flex align-items-center">
                                                <img src={contact.icon} alt={contact.title} style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    objectFit: 'contain',
                                                    marginRight: '1rem'
                                                }}/>
                                                <div>
                                                    <h3 className="h5 mb-1">{contact.title}</h3>
                                                    <p className="mb-0 opacity-75">{contact.value}</p>
                                                </div>
                                            </div>
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pied de Page */}
            <footer className="footer ip-animated-section">
                <div className="container">
                    <div className="row g-5">
                        <div className="col-lg-4">
                            <h3 className="h5 text-white mb-4">NTR Finance</h3>
                            <p className="text-white-50">La solution simple et sécurisée pour vos transferts d'argent
                                vers l'Afrique.</p>
                        </div>
                        <div className="col-lg-4">
                            <h3 className="h5 text-white mb-4">Liens rapides</h3>
                            <ul className="list-unstyled footer-links">
                                <li className="mb-2"><Link to="/" className="text-white-50">Accueil</Link></li>
                                <li className="mb-2"><Link to="/login" className="text-white-50">Connexion</Link></li>
                                <li className="mb-2"><Link to="/register" className="text-white-50">Inscription</Link>
                                </li>
                                <li><Link to="/privacy_policy" className="text-white-50">Politique de confidentialité</Link></li>
                                <li><Link to="/account-deletion-request" className="text-white-50">
                                    Suppression de Compte
                                </Link></li>
                            </ul>
                        </div>
                        <div className="col-lg-4">
                            <h3 className="h5 text-white mb-4">Contact</h3>
                            <ul className="list-unstyled text-white-50">
                                <li className="mb-2">5374 rue bossuet, Lévis, Québec</li>
                                <li className="mb-2">support@ntrfinancial.com</li>
                                <li className="mb-2">admin@ntrfinancial.com</li>
                                <li>+1 587-989-4933</li>
                                <h3 className="h5 text-white mt-4">Permis D’ESM du Québec</h3>
                                <li className="mb-2">Numéro : 0000000000018805</li>
                            </ul>
                        </div>
                    </div>
                    <hr className="my-5 border-white-10"/>
                    <div className="text-center">
                        <p className="mb-0 text-white-50">&copy; {new Date().getFullYear()} NTR Finance. Tous droits
                            réservés.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default IndexPage;